import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function removeApplicationAuth(applicationAuthId: string) {
  await api.backend.delete(`/application-auth/${applicationAuthId}`)
}

export function useRemoveApplicationAuth(onSuccess?: () => void) {
  const { t } = useTranslation('sso')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationKey: ['/application-auth/id'],
    mutationFn: removeApplicationAuth,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Trash2,
          title: t('applications.remove_application_auth.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application-auth/findOne'] })
      onSuccess?.()
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('applications.remove_application_auth.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('applications.remove_application_auth.toast_error.description'),
        },
      })
    },
  })

  const handleRemoveApplicationAuth = (
    applicationAuthId: string,
    onCancel?: () => void,
  ) => {
    handleModal({
      type: 'danger',
      title: t('applications.remove_application_auth.modal.title'),
      description: t('applications.remove_application_auth.modal.description'),
      confirmText: t(
        'applications.remove_application_auth.modal.button_confirm',
      ),
      onCancel,
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(applicationAuthId).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleRemoveApplicationAuth,
  }
}
